import { DefaultDeductionSourceEnum } from '@ben/shared/types/benefit';
import { type IModularBenefitDetail } from '@ben/shared/types/columbia';
import type {
  AirtableFields,
  AirtableForm,
  ConditionalApprovalsFields,
  ConditionalApprovalsForm,
  DocumentsFields,
  DocumentsForm,
  EligibilityRulesFields,
  EligibilityRulesForm,
  EnrolmentConfigFields,
  EnrolmentConfigForm,
  EnrolmentSettingsFields,
  EnrolmentSettingsForm,
  FeaturesFields,
  FeaturesForm,
  GeneralForm,
  GeneralFormFields,
  HeroImageFields,
  HeroImageForm,
  ImageFields,
  ImageForm,
  InternalNotesFields,
  InternalNotesForm,
  LifeEventsFields,
  LifeEventsForm,
  ManagerFields,
  ManagerForm,
  PayrollFields,
  PayrollForm,
  PricesFields,
  PricesForm,
  RateTableFields,
  RateTableForm,
  ReportingFields,
  ReportingForm,
  StepsFields,
  StepsForm,
  WindowsFields,
  WindowsForm,
} from '../elements/types';
import { cleanupRestrictions } from '../life-events/hooks/life-event-restriction-form.utils';
import useSectionForm from './use-section-form';

interface BenefitSectionForms {
  airtableForm: AirtableForm;
  generalForm: GeneralForm;
  enrolmentSettingsForm: EnrolmentSettingsForm;
  enrolmentConfigForm: EnrolmentConfigForm;
  windowsForm: WindowsForm;
  lifeEventsForm: LifeEventsForm;
  managerForm: ManagerForm;
  rateTableForm: RateTableForm;
  pricesForm: PricesForm;
  payrollForm: PayrollForm;
  reportingForm: ReportingForm;
  conditionalApprovalsForm: ConditionalApprovalsForm;
  benefitImageForm: ImageForm;
  benefitHeroImageForm: HeroImageForm;
  benefitDocumentsForm: DocumentsForm;
  benefitFeaturesForm: FeaturesForm;
  eligibilityRulesForm: EligibilityRulesForm;
  internalNotesForm: InternalNotesForm;
  stepsForm: StepsForm;
}

export const useBenefitSectionForms = (
  benefit: IModularBenefitDetail
): BenefitSectionForms => {
  const generalForm = useSectionForm<GeneralFormFields>({
    active: benefit.active,
    status: benefit.status,
    newBenefitType: benefit.newBenefitType,
    enrolmentType: benefit.enrolmentType,
    employer: benefit.employer,
    benefitProgramme: benefit.benefitProgramme,
    categories: benefit.categories,
    benefitRequirements: benefit.benefitRequirements,
    externalLinkUrl: benefit.externalLinkUrl,
    outboundProvider: benefit.outboundProvider,
    optInButtonText: benefit.optInButtonText,
    optOutReasonsGroupType: benefit.optOutReasonsGroupType,
    optOutReasonsGroup: benefit.optOutReasonsGroup,
    customModals: benefit.customModals ?? [],
    ...(Object.fromEntries(
      Object.entries(benefit).filter(
        ([key]) =>
          key.startsWith('name') ||
          key.startsWith('displayName') ||
          key.startsWith('shortDescription') ||
          key.startsWith('ctaText') ||
          key.startsWith('content')
      )
    ) as Partial<IModularBenefitDetail>),
  });

  const airtableForm = useSectionForm<AirtableFields>({
    airtableId: benefit.airtableId,
  });

  const enrolmentSettingsForm = useSectionForm<EnrolmentSettingsFields>({
    startDate: benefit.startDate,
    renewalDate: benefit.renewalDate,
    policyLengthMonths: benefit.policyLengthMonths,
    activationDelay: benefit.activationDelay,
    delayReferenceMode: benefit.delayReferenceMode,
    changeDelay: benefit.changeDelay,
    deactivationDelay: benefit.deactivationDelay,
    autoActivating: benefit.autoActivating,
    deactivateEnrolmentsWithProgramme:
      benefit.deactivateEnrolmentsWithProgramme,
    requiresActivationApproval: benefit.requiresActivationApproval,
    requiresDeactivationApproval: benefit.requiresDeactivationApproval,
    showEnrolments: benefit.showEnrolments,
    showChanges: benefit.showChanges,
    showPaymentHistory: benefit.showPaymentHistory,
    enableRequests: benefit.enableRequests,
  });

  const enrolmentConfigForm = useSectionForm<EnrolmentConfigFields>({
    employer: benefit.employer,
    enrollmentConfig: benefit.enrollmentConfig,
    startDateOverride: benefit.startDateOverride,
    startDateOverrideField: benefit.startDateOverrideField,
    endDateOverride: benefit.endDateOverride,
    endDateOverrideField: benefit.endDateOverrideField,
    optInTimestampOverride: benefit.optInTimestampOverride,
    optOutTimestampOverride: benefit.optOutTimestampOverride,
    keepEnrolledUntilSpa: benefit.keepEnrolledUntilSpa,
    statePensionAgeConfig: benefit.statePensionAgeConfig,
    setContactOptInToEmployeeOptIn: benefit.setContactOptInToEmployeeOptIn,
  });

  const windowsForm = useSectionForm<WindowsFields>({
    benefitWindows: benefit.benefitWindows,
    closeConfigWindowAfterOptIn: benefit.closeConfigWindowAfterOptIn,
    cancelEnrolmentOnOptOutDuringOptInWindow:
      benefit.cancelEnrolmentOnOptOutDuringOptInWindow,
    closeOptInWindowAfterOptOut: benefit.closeOptInWindowAfterOptOut,
    closeWindowsOnFulfillment: benefit.closeWindowsOnFulfillment,
    hideClosedConfigWindowBanner: benefit.hideClosedConfigWindowBanner,
    lifeEvents: benefit.lifeEvents,
  });

  const managerForm = useSectionForm<ManagerFields>({
    managedBy: benefit.managedBy,
    broker: benefit.broker,
    provider: benefit.provider,
    groupMembershipNumber: benefit.groupMembershipNumber,
    policyNumberType: benefit.policyNumberType,
    clientBenefitCustomCode: benefit.clientBenefitCustomCode,
    product: benefit.product,
    isAuditable: benefit.isAuditable ?? false,
  });

  const rateTableForm = useSectionForm<RateTableFields>({
    additionalConfig: {
      rateTables: benefit.additionalConfig?.rateTables ?? [],
    },
  });

  const pricesForm = useSectionForm<PricesFields>({
    id: benefit.id,
    currencyOverride: benefit.currencyOverride,
    payDeductionType: benefit.payDeductionType,
    allowEmployeePayDeductionChoice: benefit.allowEmployeePayDeductionChoice,
    ageCalculateFrom: benefit.ageCalculateFrom,
    remainderAttribution: benefit.remainderAttribution,
    salaryCalculateFrom: benefit.salaryCalculateFrom,
    allowanceImmediateDeduction: benefit.allowanceImmediateDeduction,
    includeInNmwCalculation: benefit.includeInNmwCalculation,
    deductionLogic: benefit.deductionLogic,
    showPrice: benefit.showPrice,
    showPaymentSources: benefit.showPaymentSources,
    showTotalLoanBanner: benefit.showTotalLoanBanner ?? false,
    paymentSource: benefit.paymentSource,
    defaultDeductionSource:
      benefit.defaultDeductionSource ||
      DefaultDeductionSourceEnum.ALLOWANCE_FIRST,
    resetDeductionsAtRenewal: benefit.resetDeductionsAtRenewal ?? false,
    recalculatePriceAfterEmployeeSalaryChange:
      benefit.recalculatePriceAfterEmployeeSalaryChange,
    recalculatePriceAfterEmployeeChange:
      benefit.recalculatePriceAfterEmployeeChange,
    recalculationConfigs: benefit.recalculationConfigs,
    additionalConfig: {
      id: benefit.additionalConfig?.id,
      subPrices: benefit.additionalConfig?.subPrices ?? [],
      subPricesNeedTesting:
        benefit.additionalConfig?.subPricesNeedTesting ?? false,
    },
  });

  const payrollForm = useSectionForm<PayrollFields>({
    postCutoffBehaviour: benefit.postCutoffBehaviour,
    prorateForPayroll: benefit.prorateForPayroll,
    pensionChangeField: benefit.pensionChangeField,
  });

  const reportingForm = useSectionForm<ReportingFields>({
    additionalConfig: {
      tags: benefit.additionalConfig?.tags ?? [],
    },
    productType: benefit.productType,
    countries: benefit.countries ?? [],
  });

  const conditionalApprovalsForm = useSectionForm<ConditionalApprovalsFields>({
    employer: benefit.employer,
    conditionalApprovalConfigs: benefit.conditionalApprovalConfigs,
  });

  const benefitImageForm = useSectionForm<ImageFields>({
    benefitImage: benefit.benefitImage,
  });

  const benefitHeroImageForm = useSectionForm<HeroImageFields>({
    benefitHeroImage: benefit.benefitHeroImage,
  });

  const benefitDocumentsForm = useSectionForm<DocumentsFields>({
    documents: benefit.documents,
  });

  const benefitFeaturesForm = useSectionForm<FeaturesFields>({
    features: benefit.features,
  });

  const eligibilityRulesForm = useSectionForm<EligibilityRulesFields>({
    employer: benefit.employer,
    eligibilityRules: benefit.eligibilityRules,
  });

  const internalNotesForm = useSectionForm<InternalNotesFields>({
    internalNotes: benefit.internalNotes,
  });

  const stepsForm = useSectionForm<StepsFields>({
    additionalConfig: {
      steps: benefit.additionalConfig?.steps ?? [],
    },
  });

  const lifeEventsForm = useSectionForm<LifeEventsFields>({
    lifeEventPolicies: benefit.lifeEventPolicies?.map((policy) => ({
      ...policy,
      restrictions: cleanupRestrictions(policy.restrictions ?? []),
    })),
  });

  return {
    airtableForm,
    generalForm,
    enrolmentSettingsForm,
    enrolmentConfigForm,
    windowsForm,
    lifeEventsForm,
    managerForm,
    rateTableForm,
    pricesForm,
    payrollForm,
    reportingForm,
    conditionalApprovalsForm,
    benefitImageForm,
    benefitHeroImageForm,
    benefitDocumentsForm,
    benefitFeaturesForm,
    eligibilityRulesForm,
    internalNotesForm,
    stepsForm,
  };
};

export default useBenefitSectionForms;
